import { useState, useRef, useEffect } from "react";
import { Rnd } from "react-rnd";
import { ReactSketchCanvas } from "react-sketch-canvas";
import CanvasDraw from "@win11react/react-canvas-draw";
import {
    FaArrowRight,
    FaPlay,
    FaPause,
    FaArrowLeft,
} from 'react-icons/fa';
import {
    TbRewindBackward10,
    TbRewindForward10
} from 'react-icons/tb'
import {
    FaArrowRotateRight,
    FaForwardStep,
    FaBackwardStep
} from 'react-icons/fa6';

import { config } from "../../config";
import 'reactjs-popup/dist/index.css';
import { useStores } from "../../stores/store";
import { useNavigate } from "react-router-dom";
import { Action, ActionType, DataType, Widget, WidgetType } from "../../models/models";
import { useQuery } from "@tanstack/react-query";
import { db } from "../../models/db";
import { useTranslation } from "react-i18next";
import Loader from "../common/loader";
import { rgbToHex } from "../../utils/utils";

type NavigationProps = {
    side: string;
    isOpen: boolean;
    setControlBarOpen: any;
};

type ControlProps = {
    onReset: any;
    onPlay: any;
    onPause: any;
    onBack: any;
    onForward: any;
    onNextLesson: any;
    onPrevLesson: any;
    side: string;
    isOpen: boolean;
    setControlBarOpen: any;
};

function NavigationBar(props: NavigationProps) {
    const { auth } = useStores();
    const { t } = useTranslation();

    const currentLesson = auth.getLocalLesson();
    let navigate = useNavigate();
    const lessonsAvailable = auth.getLocalLessonGroup()?.lessons.sort((a, b) => (a.name < b.name) ? -1 : 1);
    const touchEnabled = useRef<boolean>(false);
    const [primaryColor, setPrimaryColor] = useState<string>("#EF7d00");

    useEffect(() => {
		const rootStyle = getComputedStyle(document.documentElement);
		const pColor = rgbToHex(rootStyle.getPropertyValue("--color-primary").trim());
		setPrimaryColor(pColor);

        if (currentLesson.color) {
            setPrimaryColor(currentLesson.color);
        }
    }, [])

    useEffect(() => {
        touchEnabled.current = false;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)
    }, [props.isOpen]);

    if (props.side == "none" || props.isOpen == false)
        return <></>;

    const goToLesson = (lesson: any) => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        auth.saveLocalLesson(lesson);

		navigate(0);
	}

    const back = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        if (auth.getLocalEducationLevel() == null)
            navigate('/user/personal-area');
        else
            navigate('/user/lesson-group-selection');
	}

    return (
        <>
            <div style={{ borderColor: primaryColor }} className={`${props.side == "right" ? "right-0 rounded-l-3xl border-l-2" : "left-0 rounded-r-3xl border-r-2"} absolute bottom-0 bg-white w-64 h-full flex flex-col border-t-2 border-b-2 justify-evenly items-start pr-3`}>
                { lessonsAvailable?.map((lesson, index) => (
                    <div key={lesson.id} onMouseDown={() => goToLesson(lesson)} onTouchStart={() => config.isMiriOrPro() ? goToLesson(lesson) : null} className="flex items-center rounded-md px-4 cursor-pointer">
                        { currentLesson.id == lesson.id ?
                            <span style={{ color: primaryColor, borderColor: primaryColor, borderWidth: 2 }} className="bg-white rounded-md w-6 h-6 text-center mr-2">{index+1}</span>
                        :
                            <span style={{ backgroundColor: primaryColor }} className="text-white rounded-md w-6 h-6 text-center mr-2">{index+1}</span>
                        }
                        <div className="flex flex-col">
                            <span className="text-sm text-black cursor-pointer">{lesson.title}</span>
                            <span className="text-sm text-black cursor-pointer">{lesson.description}</span>
                        </div>
                     </div>
                ))}

                <div onMouseDown={() => back()} onTouchStart={() => config.isMiriOrPro() ? back() : null} className="flex items-center rounded-md px-4 pb-12 cursor-pointer">
                    <span style={{ backgroundColor: primaryColor }} className="flex flex-row justify-center items-center text-white rounded-md w-6 h-6 text-center mr-2"><FaArrowLeft /></span>
                    <div className="flex flex-col">
                        <span className="text-xl font-bold text-black cursor-pointer uppercase">{t('goBack')}</span>
                    </div>
                </div>

            </div>
            <div onMouseDown={() => props.setControlBarOpen()} onTouchStart={() => config.isMiriOrPro() ? props.setControlBarOpen() : null} style={{ backgroundColor: primaryColor }} className={`${ props.side == "right" ? "right-64 rounded-l-3xl" : "left-64 rounded-r-3xl"} cursor-pointer absolute bottom-1/4 w-20 h-20 flex flex-col justify-center items-center text-white`}>
                {
                    props.side == "right" ?
                        <FaArrowRight />
                    :
                        <FaArrowLeft />
                }
            </div>
        </>
    );
}

function ControlBar(props: ControlProps) {
    const { auth } = useStores();
    const lesson = auth.getLocalLesson();
    const [primaryColor, setPrimaryColor] = useState<string>("#EF7d00");

    useEffect(() => {
        const rootStyle = getComputedStyle(document.documentElement);
        const pColor = rgbToHex(rootStyle.getPropertyValue("--color-primary").trim());
        setPrimaryColor(pColor);

        if (lesson.color) {
            setPrimaryColor(lesson.color);
        }
    }, [])

    if (props.side == "none" || props.isOpen == false)
        return <></>;

    return (
        <>
            <div style={{ borderColor: primaryColor }} className={`${props.side == "right" ? "absolute right-0" : "absolute left-0"} top-0 h-3/4 w-20 flex flex-col`}>
                <div style={{ borderColor: primaryColor }} className={`${props.side == "right" ? "rounded-l-3xl border-l-2" : "rounded-r-3xl border-r-2"} h-5/6 bg-white py-2 flex flex-col border-t-2 border-b-2 justify-evenly items-center space-y-2`}>
                    <div onMouseDown={() => props.onReset()} onTouchStart={() => config.isMiriOrPro() ? props.onReset() : null} style={{ color: primaryColor }} className="cursor-pointer text-2xl">
                        <FaArrowRotateRight />
                    </div>
                    <div onMouseDown={() => props.onForward()} onTouchStart={() => config.isMiriOrPro() ? props.onForward() : null} style={{ color: primaryColor }} className="cursor-pointer text-2xl">
                        <TbRewindForward10 />
                    </div>
                    <div onMouseDown={() => props.onBack()} onTouchStart={() => config.isMiriOrPro() ? props.onBack() : null} style={{ color: primaryColor }} className="cursor-pointer text-2xl">
                        <TbRewindBackward10 />
                    </div>
                    <div onMouseDown={() => props.onPlay()} onTouchStart={() => config.isMiriOrPro() ? props.onPlay() : null} style={{ color: primaryColor }} className="cursor-pointer text-2xl">
                        <FaPlay />
                    </div>
                    <div onMouseDown={() => props.onPause()} onTouchStart={() => config.isMiriOrPro() ? props.onPause() : null} style={{ color: primaryColor }} className="cursor-pointer text-2xl">
                        <FaPause />
                    </div>
                    <div onMouseDown={() => props.onNextLesson()} onTouchStart={() => config.isMiriOrPro() ? props.onNextLesson() : null} style={{ color: primaryColor }} className="cursor-pointer text-2xl">
                        <FaForwardStep />
                    </div>
                    <div onMouseDown={() => props.onPrevLesson()} onTouchStart={() => config.isMiriOrPro() ? props.onPrevLesson() : null} style={{ color: primaryColor }} className="cursor-pointer text-2xl">
                        <FaBackwardStep />
                    </div>
                </div>
                <div onMouseDown={() => props.setControlBarOpen()} onTouchStart={() => config.isMiriOrPro() ? props.setControlBarOpen() : null} style={{ backgroundColor: primaryColor }} className={`${props.side == "right" ? "rounded-l-3xl" : "rounded-r-3xl"} cursor-pointer mt-2 text-white w-20 h-1/6 flex flex-col justify-evenly items-center`}>
                    {
                        props.side == "right" ?
                            <FaArrowLeft />
                        :
                            <FaArrowRight />
                    }
                </div>
            </div>
        </>
    );
}

export default function NewLessonComposerPage() {
    const { t } = useTranslation();
    const { auth } = useStores();
    let navigate = useNavigate();
    const lesson = auth.getLocalLesson();

    const [dxControlBarOpen, setDxControlBarOpen] = useState<boolean>(true);
    const [sxControlBarOpen, setSxControlBarOpen] = useState<boolean>(true);

    const [screenSize, setScreenSize] = useState([0, 0]);
    const [screenRatioStr, setScreenRatioStr] = useState("48_9");
    const [screenRatio, setScreenRatio] = useState(48/9);
    const [widgets, setWidgets] = useState<Widget[]>([]);
    const [audios, setAudios] = useState<Widget[]>([]);
    const canvasRefs = useRef<any[]>([]);
    const [actions, setActions] = useState<Action[]>([]);
    const [firstActionElements, setFirstActionElements] = useState<Widget[]>([null, null, null]);

    const [background, setBackground] = useState<Widget>();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [brushColor, setBrushColor] = useState<string>("#FF0000");

    const [title, setTitle] = useState<string>("");
    const [instructions, setInstructions] = useState<string>("");

    const [isApp, setIsApp] = useState<boolean>(true);

    const screenRef = useRef(null);
    const vidRef = useRef<HTMLVideoElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const touchEnabled = useRef<boolean>(true);

    const objectUrlCacheRef = useRef<Map<string, string>>(new Map());

    const getJwtToken = (): string | null => {
        try {
            return localStorage.getItem('access_token');
        } catch {
            return null;
        }
    };

    const fullUrl = (u?: string) => {
        if (!u) return '';
        if (u.startsWith('http')) return u;
        return config.mediaEndpoint + u;
    };

    const getBlobFromDB = async (url: string): Promise<Blob | null> => {
        try {
            const row = await db.videoList.where('url').equals(url).first();
            return row?.data ?? null;
        } catch {
            return null;
        }
    };

    const saveBlobToDB = async (url: string, blob: Blob) => {
        try {
            await db.videoList.put({ url, data: blob });
        } catch (e) {
            console.warn('Failed saving blob to DB', url, e);
        }
    };

    const fetchBlobWithJwt = async (url: string): Promise<Blob> => {
        const jwt = getJwtToken();
        const headers: Record<string, string> = {};
        if (jwt) headers['Authorization'] = `Bearer ${jwt}`;

        const resp = await fetch(url, {
            method: 'GET',
            headers,
            //cache: 'no-store',
            //credentials: 'include', // utile se il backend usa anche cookie/samesite
        });
        if (!resp.ok) throw new Error(`Failed fetching ${url} (${resp.status})`);
        return await resp.blob();
    };

    /** Restituisce SEMPRE un objectURL per quel media, con priorità IndexedDB e fetch autenticato in fallback */
    const ensureObjectUrl = async (rawUrl: string): Promise<string> => {
        const url = fullUrl(rawUrl);
        const cached = objectUrlCacheRef.current.get(url);
        if (cached) return cached;

        let blob = await getBlobFromDB(url);
        if (!blob) {
            blob = await fetchBlobWithJwt(url);
            await saveBlobToDB(url, blob);
        }
        const objUrl = URL.createObjectURL(blob);
        objectUrlCacheRef.current.set(url, objUrl);
        return objUrl;
    };

    const revokeAllObjectUrls = () => {
        objectUrlCacheRef.current.forEach((objUrl) => {
            URL.revokeObjectURL(objUrl);
        });
        objectUrlCacheRef.current.clear();
    };

    useEffect(() => {
        if (!config.isApp())
            setIsApp(false);
        return () => {
            revokeAllObjectUrls();
        };
    }, []);

    useEffect(() => {
        function updateSize() {
            repositionateWidgets();
        }
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgets]);

    const { refetch } = useQuery({
        enabled: false,
        queryKey: ['lesson-content', lesson?.id],
        staleTime: Infinity,
        retry: 0,
        queryFn: () =>
          auth.getLesson(lesson.id).then((res: any) => {
            parse(res.data.content);
            return res.data.content;
        }),
    });

    const lookupWidget = (list: Widget[], id: string) => {
        return list.find((element) => element.id === id);
    }

    const updateFirstElementsAtIndex = (idx: number, nuovoValore: Widget) => {
        setFirstActionElements(prev => {
            const newArray = prev.map((el, i) => (i === idx ? nuovoValore : el));
            return newArray;
        });        
    }

    const getScreenNumber = (widget: Widget) => {
        if (screenRatioStr != "48_9") {
            return 0;
        }
        // attuale comportamento: sempre 0
        return 0;
    }

    const playCallback = () => {
        if (audioRef.current)
            audioRef.current.play();
    }

    const playAudio = (audio: Widget) => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.removeEventListener("canplaythrough", playCallback);
        }

        audioRef.current = new Audio(audio.dataBlob as any);
        audioRef.current.addEventListener("canplaythrough", playCallback);
    }

    const playSuccess = () => {
        let audioSuccess = new Audio("/audio/success.wav");
        audioSuccess.addEventListener("canplaythrough", audioSuccess.play);
    }

    const playFailure = () => {
        let audioFailure = new Audio("/audio/failure.wav");
        audioFailure.addEventListener("canplaythrough", audioFailure.play);
    }

    const performClickAction = (widgetId: string) => {
        if (!touchEnabled.current)
            return;

        setTimeout(() => {
            touchEnabled.current = true;
        }, 250)

        touchEnabled.current = false;

        let localWidgets = [...widgets];

        let widgetTouched = lookupWidget(localWidgets, widgetId);
        let screenIdxWidgetTouched = getScreenNumber(widgetTouched!);
        let isFailed = false;

        for (let i = 0; i < actions.length; i++) {
            let action = actions[i];

            if (action.actionType === ActionType.playAudio) {
                if (widgetId !== action.widget1)
                    continue;

                let audioWidget = lookupWidget(audios, action.widget2)!;
                playAudio(audioWidget);
            } else if (action.actionType === ActionType.show) {
                if (widgetId !== action.widget1)
                    continue;

                let widget2 = lookupWidget(localWidgets, action.widget2)!;
                widget2.visible = !widget2.visible;
            } else if (action.actionType === ActionType.showUnique) {
                if (widgetId !== action.widget1)
                    continue;

                let widget2 = lookupWidget(localWidgets, action.widget2)!;
                widget2.visible = !widget2.visible;

                if (widget2.visible == true) {
                    /* hide all other widgets */
                    for (let j = 0; j < actions.length; j++) {

                        if (actions[j].actionType === ActionType.show || 
                            actions[j].actionType === ActionType.showUnique) {

                                if (actions[j].widget2 === widget2.id)
                                    continue;

                                let myWidget = lookupWidget(localWidgets, actions[j].widget2)!;
                                myWidget.visible = false;
                        }
                    }
                }
            } else if (action.actionType === ActionType.match) {
                let widget1 = lookupWidget(localWidgets, action.widget1)!;
                let widget2 = lookupWidget(localWidgets, action.widget2)!;
                
                let screenIdxWidget1 = getScreenNumber(widget1);
                let screenIdxWidget2 = getScreenNumber(widget2);

                if (screenIdxWidget1 !== screenIdxWidgetTouched) {
                    continue;
                }
                if (screenIdxWidget2 !== screenIdxWidgetTouched) {
                    continue;
                }

                if (widgetId === action.widget1) {
                    if (firstActionElements[screenIdxWidget1]) {
                        updateFirstElementsAtIndex(screenIdxWidget1, null as any);
                        continue;
                    }
                    updateFirstElementsAtIndex(screenIdxWidget1, widget1);
                }

                if (widgetId === action.widget2) {
                    if (!firstActionElements[screenIdxWidget2]) {
                        continue;
                    }

                    if (firstActionElements[screenIdxWidget2]!.id !== action.widget1) {
                        isFailed = true;
                        continue;
                    }

                    widget1.posX = widget2.posX;
                    widget1.posY = widget2.posY;
                    widget1.width = widget2.width;
                    widget1.height = widget2.height;

                    updateFirstElementsAtIndex(screenIdxWidget2, null as any);

                    widget1.ref.updatePosition({
                        x: widget1.posX,
                        y: widget1.posY});

                    widget1.ref.updateSize({
                        width: widget1.width,
                        height: widget1.height
                    })

                    widget2.visible = false;

                    playSuccess();
                }
            } else if (action.actionType === ActionType.truefalse) {
                if (widgetId === action.widget1 || widgetId === action.widget2) {
                    let myWidget = lookupWidget(localWidgets, widgetId)!;
                    if (widgetId === action.widget1) {
                        myWidget.isRight = true;
                    }
                    if (widgetId === action.widget2) {
                        myWidget.isFailed = true;
                    }
                }
                window.setTimeout(() => clearAnimations(), 1000);
            }
        }
        if (isFailed) {
            let myWidget1 = lookupWidget(localWidgets, widgetId)!;
            myWidget1.isFailed = true;

            window.setTimeout(() => clearAnimations(), 1000);

            updateFirstElementsAtIndex(screenIdxWidgetTouched, null as any);
            playFailure();
        }

        setWidgets(localWidgets);
    }

    const clearAnimations = () => {
        let localWidgets = [...widgets];

        for (let i = 0; i < localWidgets.length; i++) {
            localWidgets[i].isRight = false as any;
            localWidgets[i].isFailed = false as any;
        }

        setWidgets(localWidgets);
    }
 
    const resetPositions = () => {
        let localWidgets = [...widgets];

        localWidgets.forEach((widget) => {
            widget.posX = widget.startingPosX;
            widget.posY = widget.startingPosY;

            widget.ref.updatePosition({
                x: widget.startingPosX,
                y: widget.startingPosY
            });

            widget.visible = widget.originalVisibility;
        });

        setWidgets(localWidgets);

        canvasRefs.current?.forEach((canvas) => {
            canvas.eraseAll();
        });

        if (vidRef && vidRef.current) {
            vidRef.current.currentTime = 0;
        }
    }

    const repositionateWidgets = () => {
        let localWidgets = [...widgets];

        for (let index = 0; index < localWidgets.length; index++) {
            let widget = localWidgets[index];

            let currentWidth = screenSize[0] == 0 ? window.innerWidth : screenSize[0];
            let currentHeight = screenSize[1] == 0 ? window.innerWidth / (screenRatio) : screenSize[1];

            let newScreenWidth = window.innerWidth;
            let newScreenHeight = window.innerWidth / (screenRatio);

            if (currentWidth !== newScreenWidth || currentHeight !== newScreenHeight) {
                widget.startingPosX = (newScreenWidth * widget.startingPosX) / currentWidth;
                widget.startingPosY = (newScreenHeight * widget.startingPosY) / currentHeight;

                widget.posX = (newScreenWidth * widget.posX) / currentWidth;
                widget.posY = (newScreenHeight * widget.posY) / currentHeight;

                widget.width = (newScreenWidth * widget.width) / currentWidth;
                widget.height = (newScreenHeight * widget.height) / currentHeight;
            }

            widget.ref.updatePosition({
                x: widget.posX,
                y: widget.posY});
                
            widget.ref.updateSize({
                width: widget.width,
                height: widget.height
            })
        }

        setWidgets(localWidgets);
        setScreenSize([window.innerWidth, window.innerWidth / (screenRatio)]);
    }

    // backgroundDimension : parsedDimension = currentScreenDimension : x
    const scaleDown = (originalBackgroundDimension: number, currentDimension: number, screenDimension: number) => {
        return (currentDimension * screenDimension) / originalBackgroundDimension;
    }

    // Utility: ottieni naturalWidth/Height da un objectURL
    const getImageNaturalSizeFromObjectUrl = (objUrl: string): Promise<{w:number,h:number}> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = (e) => reject(e);
            img.src = objUrl;
        });
    };

    const parse = async (jsonBody: any) => {
        setIsLoading(true);

        let localWidgets: Widget[] = [];
        let localAudios: Widget[] = [];
        let localActions: Action[] = [];

        jsonBody = JSON.parse(jsonBody)['data'];

        let parsedRatio = jsonBody['ratio'];
        let screenR: number;

        if (parsedRatio == undefined || parsedRatio == "48_9") {
            setScreenRatioStr("48_9");
            setScreenRatio(48/9);
            screenR = 48/9;
        } else {
            setScreenRatioStr("16_9");
            setScreenRatio(16/9);
            screenR = 16/9;
        }

        let parsedT = jsonBody['title'];
        if (parsedT != null && parsedT != undefined) {
            setTitle(jsonBody['title'])
        } else {
            setTitle("");
        }

        let parsedI = jsonBody['instructions'];
        if (parsedI != null && parsedI != undefined) {
            setInstructions(jsonBody['instructions'])
        } else {
            setInstructions("");
        }

        let parsedB = jsonBody['background'];

        let originalBackgroundWidth = parsedB.width;
        let originalBackgroundHeight = originalBackgroundWidth / (screenR);

        // BACKGROUND: blob se non externalVideo
        let backgroundObjUrl: string | null = null;
        if (parsedB.dataType !== DataType.externalVideo) {
            backgroundObjUrl = await ensureObjectUrl(parsedB.dataUrl);
        }

        let widgetB: Widget = {
            id: parsedB.id,
            startingPosX: parsedB.startingPosX,
            startingPosY: parsedB.startingPosY,
            posX: parsedB.posX,
            posY: parsedB.posY,
            startingWidth: window.innerWidth,
            startingHeight: window.innerWidth / (screenR),
            width: window.innerWidth,
            height: window.innerWidth / (screenR),
            visible: parsedB.visible,
            originalVisibility: parsedB.visible,
            dataUrl: fullUrl(parsedB.dataUrl),
            dataRaw: parsedB.dataRaw,
            dataBlob: backgroundObjUrl, // SOLO objectURL (o null se externalVideo)
            dataName: parsedB.dataName,
            dataType: parsedB.dataType,
            loadedFromCache: !!backgroundObjUrl,
            type: parsedB.type
        };
        setBackground(widgetB);

        // WIDGETS: pre-scarico e uso natural size dal blob
        for (let i = 0; i < jsonBody['widgets'].length; i++) {
            let parsedW = jsonBody['widgets'][i];

            const objUrl = await ensureObjectUrl(parsedW.dataUrl);

            // calcolo dimensioni scalando sulla base del blob
            let newHeight = parsedW.height;
            try {
                const { w: natW, h: natH } = await getImageNaturalSizeFromObjectUrl(objUrl);
                if (parsedW.width < natW) {
                    newHeight = (parsedW.width * natH) / natW;
                } else {
                    parsedW.width = natW;
                    newHeight = natH;
                }
            } catch (e) {
                console.log(e, " Cannot decode blob " + parsedW.dataUrl);
            }

            let widget: Widget = {
                id: parsedW.id,
                startingPosX: scaleDown(originalBackgroundWidth, parsedW.startingPosX, window.innerWidth),
                startingPosY: scaleDown(originalBackgroundHeight, parsedW.startingPosY, window.innerWidth / (screenR)),
                posX: scaleDown(originalBackgroundWidth, parsedW.posX, window.innerWidth),
                posY: scaleDown(originalBackgroundHeight, parsedW.posY, window.innerWidth / (screenR)),
                startingWidth: scaleDown(originalBackgroundWidth, parsedW.width, window.innerWidth),
                startingHeight: scaleDown(originalBackgroundHeight, newHeight, window.innerWidth / (screenR)),
                width: scaleDown(originalBackgroundWidth, parsedW.width, window.innerWidth),
                height: scaleDown(originalBackgroundHeight, newHeight, window.innerWidth / (screenR)),
                visible: true,
                originalVisibility: true,
                dataUrl: fullUrl(parsedW.dataUrl), // non usata a render
                dataRaw: parsedW.dataRaw,
                dataBlob: objUrl, // SEMPRE objectURL
                dataName: parsedW.dataName,
                dataType: parsedW.dataType,
                loadedFromCache: true,
                type: parsedW.type
            };
            localWidgets.push(widget);
        }

        // AUDIOS
        for (let i = 0; jsonBody['audios'] != null && i < jsonBody['audios'].length; i++) {
            let parsedA = jsonBody['audios'][i];
            const objUrl = await ensureObjectUrl(parsedA.dataUrl);

            let widget: Widget = {
                id: parsedA.id,
                startingPosX: 0,
                startingPosY: 0,
                posX: 0,
                posY: 0,
                width: 0,
                height: 0,
                startingWidth: 0,
                startingHeight: 0,
                visible: true,
                originalVisibility: true,
                dataUrl: fullUrl(parsedA.dataUrl),
                dataRaw: parsedA.dataRaw,
                dataBlob: objUrl, // objectURL
                dataName: parsedA.dataName,
                dataType: parsedA.dataType,
                loadedFromCache: true,
                type: parsedA.type
            };
            localAudios.push(widget);
        }

        // ACTIONS
        for (let i = 0; i < jsonBody['actions'].length; i++) {
            let parsedW = jsonBody['actions'][i];

            let action: Action = {
                id: parsedW.id,
                widget1: parsedW.widget1,
                widget2: parsedW.widget2,
                actionType: parsedW.actionType
            };
    
            localActions.push(action);  
        }

        /* enforce visibility */
        for (let i = 0; i < localWidgets.length; i++) {
            for (let j = 0; j < localActions.length; j++) {
                if (localActions[j].widget2 == localWidgets[i].id &&
                    (localActions[j].actionType == ActionType.show ||
                        localActions[j].actionType == ActionType.showUnique
                    ))
                    localWidgets[i].visible = false;
            }
        }

        setScreenSize([window.innerWidth, window.innerWidth / (screenR)]);
        setWidgets(localWidgets);
        setAudios(localAudios);
        setActions(localActions);
        setIsLoading(false);
    }

    const onPlay = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        if (vidRef && vidRef.current)
            vidRef.current.play();
    }

    const onPause = () => {

		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;
        if (vidRef && vidRef.current)
            vidRef.current.pause();
    }

    const onBack = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        if (vidRef && vidRef.current)
            vidRef.current.currentTime=vidRef.current.currentTime-10;
    }

    const onForward = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        if (vidRef && vidRef.current)
            vidRef.current.currentTime=vidRef.current.currentTime+10;
    }

    const onNextLesson = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        const currentLesson = auth.getLocalLesson();
        const lessonsAvailable = auth.getLocalLessonGroup().lessons.sort((a, b) => (a.name < b.name) ? -1 : 1);

        let found = false;

        for (let i = 0; i < lessonsAvailable.length; i++) {
            if (found) {
                auth.saveLocalLesson(lessonsAvailable[i]);
                navigate(0);
                break;
            }
            if (lessonsAvailable[i].id == currentLesson.id) {
                found = true;
            }
        }
    }

    const onPrevLesson = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        const currentLesson = auth.getLocalLesson();
        const lessonsAvailable = auth.getLocalLessonGroup().lessons.sort((a, b) => (a.name < b.name) ? -1 : 1);

        let found = false;

        for (let i = lessonsAvailable.length; i > 0; i--) {
            if (found) {
                auth.saveLocalLesson(lessonsAvailable[i-1]);
                navigate(0);
                break;
            }
            if (lessonsAvailable[i-1].id == currentLesson.id) {
                found = true;
            }
        }
    }

    const setDxControlBarOpenCallback = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        setDxControlBarOpen(!dxControlBarOpen);
    }

    const setSxControlBarOpenCallback = () => {
		if (!touchEnabled.current)
			return;

		setTimeout(() => {
			touchEnabled.current = true;
		}, 250)

		touchEnabled.current = false;

        setSxControlBarOpen(!sxControlBarOpen);
    }

    const back = (evt: any) => navigate('/user/lesson-group-selection');

    useEffect(() => {
        // carica la lezione solo dopo il primo render
        (async () => {
            await refetch();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isApp)
        return <div className='grid h-screen place-items-center'>
            <span>{t('userNotAllowed')}</span>
            <button className="border-2 border-primary text-primary text-center rounded-lg p-2 mt-2" onMouseDown={back}>{t('back')}</button>
        </div>

    if (isLoading)
        return <div className='grid h-screen place-items-center'>
            <Loader />
            <button className="border-2 border-primary text-primary text-center rounded-lg p-2 mt-2" onMouseDown={back}>{t('back')}</button>
        </div>

    return (
        <div className={`w-full ${screenRatioStr == "48_9" ? "aspect-[48/9]" : "aspect-[16/9]"} relative overflow-x-hidden`} ref={screenRef}>
            <div className="w-1/3 absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center">
                { title.length > 0 ? <span className="w-full text-center bg-primary p-2 text-white rounded-b-xl text-[0.5vw]">{title}</span> : <span></span> }
                { instructions.length > 0 ? <span className="w-4/5 text-center bg-white p-2 text-black rounded-b-xl text-[0.5vw]">{instructions}</span> : <span></span> }
            </div>

            {/* BACKGROUND */}
            {background && background.dataType === DataType.externalVideo && background.type === WidgetType.background && (
                <iframe className="w-full h-full object-fill" src={background.dataUrl}></iframe>
            )}
            { background && background.dataType === DataType.image && background.type === WidgetType.background && background.dataBlob && (
                <img className="w-full h-full object-fill" src={background.dataBlob as any} />
            )}
            { background && background.dataType === DataType.video && background.type === WidgetType.background && background.dataBlob && (
                <video className="w-full h-full object-fill" autoPlay loop ref={vidRef}>
                    <source src={background.dataBlob as any} type="video/mp4" />
                </video>
            )}

            {/* WIDGETS */}
            {
            widgets.map((widget, index) =>
                <Rnd
                    ref={ref => {
                        // @ts-ignore
                        widget.ref = ref;}}
                    key={widget.id}
                    enableResizing={false}
                    disableDragging={true}
                    default={{
                        x: widget.posX,
                        y: widget.posY,
                        width: widget.width,
                        height: widget.height,
                    }}
                >
                    { widget.type === WidgetType.object ? (
                        <>
                            <img draggable={false} className={`${widget.isFailed ? "animate-shake" : ""} ${widget.visible ? "visible" : "invisible"} ${(widget.id == firstActionElements[0]?.id || widget.id == firstActionElements[1]?.id || widget.id == firstActionElements[2]?.id) ? "outline outline-3 outline-primary rounded-2xl" : ""}`} src={widget.dataBlob as any} onTouchStart={() => config.isMiriOrPro() ? performClickAction(widget.id) : null} onMouseDown={() => performClickAction(widget.id)} />
                            <div style={{color: 'rgba(34, 139, 34, 0.8)' }} className={`${widget.isRight ? "opacity-100 duration-300 absolute top-1 left-1 z-10 flex justify-center items-center text-8xl text-green font-semibold" : "opacity-0 hidden"}`}> ✓</div>
                        </>
                    ) : widget.type == WidgetType.objectText ? (
                        <>
                            <div className={`${widget.isFailed ? "animate-shake" : ""} ${widget.visible ? "visible" : "invisible"} ${(widget.id == firstActionElements[0]?.id || widget.id == firstActionElements[1]?.id || widget.id == firstActionElements[2]?.id) ? "outline outline-3 outline-green rounded-2xl" : ""} w-full h-full bg-white flex-shrink-0 outline outline-2 outline-primary p-2 rounded-3xl text-center text-[0.5vw]`} onTouchStart={() => config.isMiriOrPro() ? performClickAction(widget.id) : null} onMouseDown={() => performClickAction(widget.id)}><span>{widget.dataRaw}</span></div>
                            <div style={{color: 'rgba(34, 139, 34, 0.8)' }} className={`${widget.isRight ? "opacity-100 duration-300 absolute top-1 left-1 z-10 flex justify-center items-center text-8xl text-green font-semibold" : "opacity-0 hidden"}`}> ✓</div>
                        </>
                    ) : widget.type == WidgetType.objectDestination ?
                        <>
                            <div className={`${widget.isFailed ? "animate-shake" : ""} ${widget.visible ? "visible" : "invisible"} w-full h-full bg-white flex-shrink-0 outline outline-2 outline-primary p-2 rounded-3xl text-center text-[0.5vw] text-white`} onTouchStart={() => config.isMiriOrPro() ? performClickAction(widget.id) : null} onMouseDown={() => performClickAction(widget.id)}><span>{widget.dataRaw}</span></div>
                            <div style={{color: 'rgba(34, 139, 34, 0.8)' }} className={`${widget.isRight ? "opacity-100 duration-300 absolute top-1 left-1 z-10 flex justify-center items-center text-8xl text-green font-semibold" : "opacity-0 hidden"}`}> ✓</div>
                        </>
                    : (
                        <div className="w-full h-full flex flex-col items-center">
                            <CanvasDraw className="rounded-2xl" style={{ background: "none" }}
                                canvasWidth="100%"
                                canvasHeight="100%"
                                brushColor={brushColor}
                                hideGrid={true}
                                ref={ref => {
                                    canvasRefs.current[index] = ref;
                                }}
                            />
                            <div className="mt-8 flex flex-row gap-2">
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(255, 165, 0, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(255, 165, 0, 0.8)")} style={{backgroundColor: 'rgba(255, 165, 0, 0.8)' }} className={`${brushColor == 'rgba(255, 165, 0, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(135, 206, 250, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(135, 206, 250, 0.8)")} style={{backgroundColor: 'rgba(135, 206, 250, 0.8)' }} className={`${brushColor == 'rgba(135, 206, 250, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(0, 0, 255, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(0, 0, 255, 0.8)")} style={{backgroundColor: 'rgba(0, 0, 255, 0.8)' }} className={`${brushColor == 'rgba(0, 0, 255, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(255, 255, 0, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(255, 255, 0, 0.8)")} style={{backgroundColor: 'rgba(255, 255, 0, 0.8)' }} className={`${brushColor == 'rgba(255, 255, 0, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(139, 69, 19, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(139, 69, 19, 0.8)")} style={{backgroundColor: 'rgba(139, 69, 19, 0.8)' }} className={`${brushColor == 'rgba(139, 69, 19, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(0, 0, 0, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(0, 0, 0, 0.8)")} style={{backgroundColor: 'rgba(0, 0, 0, 0.8)' }} className={`${brushColor == 'rgba(0, 0, 0, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(255, 192, 203, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(255, 192, 203, 0.8)")} style={{backgroundColor: 'rgba(255, 192, 203, 0.8)' }} className={`${brushColor == 'rgba(255, 192, 203, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(255, 0, 0, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(255, 0, 0, 0.8)")} style={{backgroundColor: 'rgba(255, 0, 0, 0.8)' }} className={`${brushColor == 'rgba(255, 0, 0, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(34, 139, 34, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(34, 139, 34, 0.8)")} style={{backgroundColor: 'rgba(34, 139, 34, 0.8)' }} className={`${brushColor == 'rgba(34, 139, 34, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                                <div onTouchStart={() => config.isMiriOrPro() ? setBrushColor("rgba(138, 43, 226, 0.8)") : null} onMouseDown={() => setBrushColor("rgba(138, 43, 226, 0.8)")} style={{backgroundColor: 'rgba(138, 43, 226, 0.8)' }} className={`${brushColor == 'rgba(138, 43, 226, 0.8)' ? "border-2 border-black" : ""} w-8 h-8 rounded-full cursor-pointer`}></div>
                            </div>
                        </div>
                    )}
                </Rnd>
            )}
            <ControlBar
                onReset={() => resetPositions()}
                onPlay={() => onPlay()}
                onPause={() => onPause()}
                onBack={() => onBack()}
                onForward={() => onForward()}
                onNextLesson={() => onNextLesson()}
                onPrevLesson={() => onPrevLesson()}
                side={config.isMiriOrPro() ? "left" : "none"}
                isOpen={sxControlBarOpen}
                setControlBarOpen={() => setSxControlBarOpenCallback()}
            />
            <NavigationBar 
                side={config.isMiriOrPro() ? "left": "none"}
                isOpen={!sxControlBarOpen}
                setControlBarOpen={() => setSxControlBarOpenCallback()}
            />
            <ControlBar
                onReset={() => resetPositions()}
                onPlay={() => onPlay()}
                onPause={() => onPause()}
                onBack={() => onBack()}
                onForward={() => onForward()}
                onNextLesson={() => onNextLesson()}
                onPrevLesson={() => onPrevLesson()}
                side={"right"}
                isOpen={dxControlBarOpen}
                setControlBarOpen={() => setDxControlBarOpenCallback()}
            />
            <NavigationBar
                side={"right"}
                isOpen={!dxControlBarOpen}
                setControlBarOpen={() => setDxControlBarOpenCallback()}
            />
        </div>
    );
}
